<template>
  <div class="pp-chooser" :style="{ '--pp-color': companyColor }">
    <button type="button" class="pp-back" @click="$emit('back')">
      <chevron-left-icon size="18" /> Voltar
    </button>
    <h1 class="pp-chooser__title">Escolha como seu perfil vai aparecer</h1>
    <p class="pp-chooser__subtitle">
      Todos os modelos usam o que você já preencheu. Dá para trocar depois, quantas vezes quiser.
    </p>

    <div class="pp-chooser__grid">
      <div
        v-for="template in templates"
        :key="template.id"
        class="pp-chooser__card"
        :class="{ 'pp-chooser__card--active': current === template.id }"
      >
        <div class="pp-chooser__thumb">
          <template-thumb :template="template.id" />
          <pro-badge v-if="isProTemplate(template.id) && !isPro" size="md" class="pp-chooser__pro" />
          <div class="pp-chooser__overlay">
            <a
              class="pp-chooser__btn pp-chooser__btn--ghost"
              :href="previewUrl(template.id)"
              target="_blank"
              rel="noopener noreferrer"
              @click="trackPreview(template.id, 'overlay')"
            >
              <eye-icon size="18" /> Pré-visualizar
            </a>
            <button
              type="button"
              class="pp-chooser__btn pp-chooser__btn--primary"
              @click="$emit('choose', template.id)"
            >
              Usar este modelo
            </button>
          </div>
        </div>
        <h3>{{ template.name }}</h3>
        <p>{{ template.description }}</p>
        <div class="pp-chooser__tags">
          <span v-for="tag in template.tags" :key="tag">{{ tag }}</span>
        </div>
        <div class="pp-chooser__mobile-actions">
          <a
            class="pp-btn"
            :href="previewUrl(template.id)"
            target="_blank"
            rel="noopener noreferrer"
            @click="trackPreview(template.id, 'mobile')"
          >
            <eye-icon size="16" /> Ver
          </a>
          <button
            type="button"
            class="pp-chooser__btn pp-chooser__btn--primary"
            @click="$emit('choose', template.id)"
          >
            Usar este modelo
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ChevronLeftIcon, EyeIcon } from "vue-feather-icons";
import TemplateThumb from "./TemplateThumb.vue";
import ProBadge from "./ProBadge.vue";
import mixin from "./mixin";
import { TEMPLATES } from "./constants";
import { templatePreviewUrl } from "@/services/publicProfile";

export default {
  name: "TemplateChooser",
  components: { TemplateThumb, ProBadge, ChevronLeftIcon, EyeIcon },
  mixins: [mixin],
  props: {
    current: { type: String, default: "" },
    proTemplates: { type: Array, default: () => [] },
    isPro: { type: Boolean, default: false },
  },
  computed: {
    templates() {
      return TEMPLATES;
    },
  },
  methods: {
    isProTemplate(id) {
      return this.proTemplates.includes(id);
    },
    previewUrl(id) {
      return templatePreviewUrl(id);
    },
    trackPreview(id, button) {
      this.ppTrack("public_profile_template_preview_opened", {
        template: id,
        template_is_pro: this.isProTemplate(id),
        origin: "template_chooser",
        button,
      });
    },
  },
};
</script>

<style lang="scss">
.pp-back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: none;
  color: #4f4f4f;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    color: #333;
  }
}

.pp-chooser {
  max-width: 1232px;
  margin: 0 auto;
  padding: 32px 24px 56px;
  font-family: Roboto, sans-serif;

  &__title {
    margin-top: 16px;
    color: #333;
    font-size: 22px;
    font-weight: 600;
  }

  &__subtitle {
    margin-top: 4px;
    color: #4f4f4f;
    font-size: 16px;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
    margin-top: 32px;

    @media (max-width: 991px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 767px) {
      grid-template-columns: 1fr;
    }
  }

  &__card {
    display: flex;
    flex-direction: column;
    padding: 12px 12px 16px;
    border: 0.5px solid #a8a8a8;
    border-radius: 16px;
    background: #fff;
    transition: border-color 0.2s ease;

    h3 {
      margin: 16px 4px 0;
      color: #333;
      font-size: 18px;
      font-weight: 500;
    }

    p {
      margin: 2px 4px 0;
      color: #696969;
      font-size: 14px;
    }

    &:hover,
    &--active {
      border-color: var(--pp-color);
    }

    &:hover .pp-chooser__overlay {
      opacity: 1;
      pointer-events: auto;
    }
  }

  &__thumb {
    position: relative;
    overflow: hidden;
    border-radius: 8px;
  }

  &__pro {
    position: absolute;
    top: 10px;
    left: 10px;
  }

  &__overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: rgba(6, 21, 43, 0.35);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;

    @media (max-width: 767px) {
      display: none;
    }
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 40px;
    padding: 0 16px;
    border: none;
    border-radius: 8px;
    font-family: Roboto, sans-serif;
    font-size: 16px;
    text-decoration: none;
    cursor: pointer;

    &--ghost {
      background: #fff;
      color: #333;
    }

    &--primary {
      background: var(--pp-color);
      color: #fff;
      font-weight: 500;
    }

    &:hover {
      filter: brightness(0.94);
    }
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 12px 4px 0;

    span {
      padding: 4px 8px;
      border: 0.5px solid #a8a8a8;
      border-radius: 4px;
      color: #333;
      font-size: 14px;
    }
  }

  &__mobile-actions {
    display: none;
    gap: 8px;
    margin-top: 16px;

    > * {
      flex: 1;
    }

    @media (max-width: 767px) {
      display: flex;
    }
  }
}
</style>
